import { Flag } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { Card, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import {
  approveTestCases,
  getDashboardTestCaseReviewData,
} from '../../../modules/project';
import CustomAvatar from '../../elements/CustomAvatar';
import Label from '../../elements/Label';
import { useNotification } from '../../../hooks/NotificationContext';

const TestcaseForReview = ({ projects }) => {
  const [testCaseData, setTestCaseData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState([]);
  const navigate = useNavigate();
  const { addNotification } = useNotification();
  const fetchTestCaseData = async () => {
    setLoading(true);
    try {
      const response = await getDashboardTestCaseReviewData(projects);
      setTestCaseData(response?.testcase_data);
    } catch (error) {
      setError(error.message || 'Failed to fetch data');
    }
    setLoading(false);
  };

  const handleCheck = (id) => {
    const selectedItem = selected?.map((item) => item);
    if (selectedItem.indexOf(id) === -1) {
      setSelected([...selectedItem, id]);
    } else {
      setSelected(selectedItem.filter((item) => item !== id));
    }
  };

  const handleSelectAll = (type) => {
    if (type === 'select') {
      setSelected(
        testCaseData
          ?.filter((testcase) => testcase.status !== 'Approved')
          .map((testcase) => testcase.id)
      );
    } else {
      setSelected([]);
    }
  };

  const handleApprove = async () => {
    try {
      await approveTestCases(selected);
      await getDashboardTestCaseReviewData(projects);
      addNotification('success', 'Test case(s) marked approved successfully.');
      setSelected([]);
    } catch (error) {
      addNotification(
        'error',
        error.message || 'Unable to mark test case(s) approved'
      );
    }
  };

  useEffect(() => {
    if (projects && projects.length > 0) fetchTestCaseData();
  }, [projects]);

  const isSelect =
    selected.length === testCaseData?.length && testCaseData?.length !== 0;

  return (
    <Container fluid>
      <Row>
        <Col>
          <h6 className="subheading-text">Assigned for Review</h6>
        </Col>
      </Row>
      {loading ? (
        <Spinner
          style={{
            display: 'block',
            margin: 'auto',
            color: 'var(--primary-color)',
          }}
        />
      ) : testCaseData && testCaseData.length === 0 ? (
        <Row>
          <Col className="text-center">
            <Card>
              <Card.Body className="card-body-list">
                {error ? (
                  <Typography variant="body2" color="error">
                    {error}
                  </Typography>
                ) : (
                  <Typography variant="body2">
                    No test cases to review
                  </Typography>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      ) : (
        <>
          <Row>
            <Col>
              <Button
                onClick={() =>
                  handleSelectAll(isSelect ? 'deselect' : 'select')
                }>
                {isSelect ? 'Deselect All' : 'Select All'}
              </Button>
              {selected.length > 0 && (
                <Button color="success" onClick={handleApprove}>
                  Approve
                </Button>
              )}
            </Col>
          </Row>
          {testCaseData &&
            testCaseData.map((testcase) => (
              <Row key={testcase.id} className="mt-2 cursor-pointer">
                <Col>
                  <Card style={{ padding: '10px !important' }}>
                    <Card.Body className="card-body-list">
                      <Container fluid>
                        <Row className="d-flex justify-content-between align-items-center">
                          <Col md="10">
                            <Box className="d-flex align-items-center">
                              <Form.Check
                                type="checkbox"
                                checked={
                                  selected.indexOf(testcase.id) > -1 ? 1 : 0
                                }
                                onChange={() => handleCheck(testcase.id)}
                              />
                              <Box
                                title={testcase.priority}
                                sx={{
                                  bgcolor: testcase.priority_color,
                                  width: '26px',
                                  height: '26px',
                                  borderRadius: '4px',
                                  ml: 2,
                                }}>
                                <Flag sx={{ color: 'white' }} />
                              </Box>
                              <Box className="ml-2" sx={{ marginLeft: '16px' }}>
                                <Typography
                                  variant="subtitle1"
                                  title="Go To Test Case"
                                  onClick={() =>
                                    navigate(
                                      `/project/${testcase?.project?.project_id}/testcases/${testcase.key}`
                                    )
                                  }>
                                  {testcase.summary}
                                </Typography>
                                <Box sx={{ display: 'flex' }}>
                                  <Typography
                                    variant="subtitle2"
                                    className="text-muted ml-2">
                                    {testcase.key} {' - '}
                                    {testcase?.project?.project_name}
                                  </Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Col>
                          <Col>
                            <Box className="d-flex align-items-center justify-content-around">
                              <Label
                                color={testcase?.status_color}
                                name={testcase?.status}
                              />
                              <CustomAvatar
                                firstName={testcase.owner_details?.first_name}
                                lastName={testcase.owner_details?.last_name}
                                size={36}
                              />
                            </Box>
                          </Col>
                        </Row>
                      </Container>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            ))}
        </>
      )}
    </Container>
  );
};

export default TestcaseForReview;
